'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Code, Cpu, Send, Loader2, Sparkles, Server, Cuboid, Ruler, Eraser, PaintBucket, Camera, Layers, CheckCircle2, ChevronRight, Share } from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';


type AgentStatus = 'idle' | 'connecting' | 'connected' | 'working';
type ChatMessage = { role: 'agent' | 'user' | 'system', content: string, action?: 'boq_export' | 'render_export' };

const QUICK_ACTIONS = [
    { id: 'bom', icon: Ruler, label: '물량/면적 산출 (BOM)', prompt: '현재 모델의 전체 재질별 적용 면적(sqm) 및 물량 산출표를 작성해줘.' },
    { id: 'purge', icon: Eraser, label: '미사용 요소 최적화 (Purge)', prompt: '사용하지 않는 컴포넌트, 재질, 레이어를 모두 퍼지(Purge)하고 모델을 최적화해줘.' },
    { id: 'material', icon: PaintBucket, label: '지정 재질 일괄 치환', prompt: '모델 내 "기본재질"로 되어있는 모든 면을 "도장 화이트"로 일괄 변경해줘.' },
    { id: 'scene', icon: Camera, label: '렌더링 샷 자동 세팅', prompt: '투시도 렌더링을 위해 카메라 FOV 65도로 주요 장면(Scene) 4개를 자동 세팅해줘.' },
];

const COMMAND_RESPONSES = [
    {
        keywords: ['물량', '면적', 'bom', '산출'],
        agentReply: '전체 모델의 파라메트릭 물량 및 면적 산출이 완료되었습니다. (벽체: 1,240㎡ / 바닥재: 450㎡ / 창호: 120㎡). 이 데이터를 기반으로 상세 내역서를 작성하시겠습니까?',
        action: 'boq_export',
        rubyCode: [
            '> [Agent] Initializing BOM (Bill of Materials) extraction engine...',
            '  model = Sketchup.active_model',
            '  materials_dict = {}',
            '  model.entities.grep(Sketchup::Face).each do |f|',
            '    mat = f.material ? f.material.name : "Default"',
            '    materials_dict[mat] ||= 0.0',
            '    materials_dict[mat] += f.area * 0.00064516 # Convert sq inches to sqm',
            '  end',
            '  puts "Extraction Complete. Generating structured JSON payload..."',
            '> [MCP] Emitting "boq_data_ready" event.'
        ]
    },
    {
        keywords: ['퍼지', '최적화', '정리'],
        agentReply: '모델 최적화가 완료되었습니다. 미사용 컴포넌트 42개, 미사용 재질 18개, 빈 레이어 5개가 삭제되었습니다. 파일 용량이 약 15% 감소했습니다.',
        rubyCode: [
            '> [Agent] Starting deep model purge operation...',
            '  model = Sketchup.active_model',
            '  model.start_operation("Deep Purge", true)',
            '  materials_purged = model.materials.purge_unused',
            '  comps_purged = model.definitions.purge_unused',
            '  layers_purged = model.layers.purge_unused',
            '  puts "Purged: #{materials_purged} Materials, #{comps_purged} Components"',
            '  model.commit_operation',
            '> [MCP] "Purge All" executed successfully and synced to NAS.'
        ]
    },
    {
        keywords: ['재질', '변경', '치환'],
        agentReply: '요청하신 재질 일괄 치환 작업이 완료되었습니다. "기본재질"이 "도장 화이트"로 적용되었으며, 총 1,024개의 페이스가 업데이트되었습니다.',
        rubyCode: [
            '> [Agent] Searching for target material nodes in ActiveEntities...',
            '  model = Sketchup.active_model',
            '  target_mat = model.materials["도장 화이트"]',
            '  count = 0',
            '  model.entities.grep(Sketchup::Face).each do |f|',
            '    if f.material.nil? || f.material.name == "기본재질"',
            '      f.material = target_mat; count += 1',
            '    end',
            '  end',
            '  puts "Replaced material for #{count} faces."',
            '> [MCP] Geometry updated. Invalidating view caches.'
        ]
    },
    {
        keywords: ['장면', '렌더링', '카메라', '뷰'],
        agentReply: '렌더링을 위한 최적 카메라 FOV(65도) 기반의 주요 장면(ISO, 거실, 복도, 외관) 셋업이 완료되었습니다. 렌더링 에이전트로 작업을 넘길까요?',
        action: 'render_export',
        rubyCode: [
            '> [Agent] Setting up architectural presentation scenes...',
            '  model = Sketchup.active_model',
            '  pages = model.pages',
            '  camera = model.active_view.camera',
            '  camera.fov = 65.0',
            '  ["ISO_View", "Exterior_Front", "Interior_Living", "Interior_Corridor"].each do |name|',
            '    pages.add(name, 119) # Save all properties',
            '  end',
            '  puts "Generated 4 standard architectural scenes with FOV 65."',
            '> [MCP] Scene states synchronized with RenderAgent.'
        ]
    }
];

export default function SKPAgentPage() {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [status, setStatus] = useState<AgentStatus>('idle');
    const [chatHistory, setChatHistory] = useState<ChatMessage[]>([
        { role: 'agent', content: '안녕하세요. SnapGRID 스케치업 전문 AI 에이전트입니다. MCP(Model Context Protocol) 기반으로 NAS의 SKP 모델과 실시간으로 연동되며, 자연어 명령으로 Ruby API를 구동하여 즉각적인 모델 수정 및 분석이 가능합니다.' }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [rubyLogs, setRubyLogs] = useState<string[]>([]);
    const endOfMessagesRef = useRef<HTMLDivElement>(null);
    const endOfLogsRef = useRef<HTMLDivElement>(null);

    const [modelData, setModelData] = useState<any>(null);

    useEffect(() => {
        if (endOfMessagesRef.current) {
            endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [chatHistory]);

    useEffect(() => {
        if (endOfLogsRef.current) {
            endOfLogsRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [rubyLogs]);

    const connectToModel = useCallback(async () => {
        setStatus('connecting');
        setRubyLogs(prev => [...prev, '> [SYSTEM] Initializing SketchUp MCP session via SnapGRID Gateway...']);
        
        await new Promise(r => setTimeout(r, 800));
        setRubyLogs(prev => [...prev, '> [NETWORK] Opening secure RPC tunnel to local NAS instance...']);
        
        await new Promise(r => setTimeout(r, 1200));
        setRubyLogs(prev => [...prev, '> [SU-BRIDGE] Connection established. Extracting full graph metadata for [ID_2531_대곡고_최종.skp]...']);
        
        await new Promise(r => setTimeout(r, 600));
        setModelData({
            name: 'ID_2531_대곡고_최종.skp',
            faces: 1542450, edges: 2830020, groups: 1245, instances: 4120,
            materials: ['노출 콘크리트 밝은색', '복층유리 24T', '적벽돌 치장쌓기', '우드 플로어링', '석고보드 위 수성페인트'],
            dimensions: { width: 146.4, depth: 82.9, height: 22.6 },
            fileSize: '428.5 MB',
            path: '/mnt/NAS/Projects/ID_2531_대곡고/Design/3D/ID_2531_대곡고_최종.skp',
            lastModified: '2026-03-13 08:32:10'
        });
        setStatus('connected');
 setChatHistory(prev =>[...prev, { role: 'system', content: ' [ID_2531_대곡고_최종.skp] 모델이 클라우드 뷰포트에 동기화되었습니다.' }]);
    }, []);

    const executeAction = async (prompt: string) => {
        if (status !== 'connected') return;
        
        setInputValue('');
        setChatHistory(prev => [...prev, { role: 'user', content: prompt }]);
        setStatus('working');

        // 매칭되는 로직 찾기
        const matchedAction = COMMAND_RESPONSES.find(cmd => 
            cmd.keywords.some(kw => prompt.toLowerCase().includes(kw))
        ) || {
            agentReply: '요청하신 코드를 성공적으로 빌드 및 실행했습니다. 모델에 변경사항이 반영되었습니다.',
            action: undefined,
            rubyCode: [
                '> [Agent] Analyzing abstract natural language request...',
                '  model = Sketchup.active_model',
                '  model.start_operation("AI Generated Action", true)',
                '  # Dynamically generated AST executing...',
                '  puts "Success: Operation completed without exceptions."',
                '  model.commit_operation',
                '> [MCP] Canvas updated.'
            ]
        };

        // 실시간 로깅 시뮬레이션
        await new Promise(r => setTimeout(r, 400));
        setRubyLogs(prev => [...prev, '', `> [Agent] Compiling Intent: "${prompt.substring(0, 30)}..."`]);
        await new Promise(r => setTimeout(r, 600));
        setRubyLogs(prev => [...prev, '> [Compiler] Yielding Ruby Supex Script:']);
        
        for (const line of matchedAction.rubyCode) {
            await new Promise(r => setTimeout(r, 200 + Math.random() * 200));
            setRubyLogs(prev => [...prev, line]);
        }

        await new Promise(r => setTimeout(r, 800));
        
        setChatHistory(prev => [...prev, { 
            role: 'agent', 
            content: matchedAction.agentReply,
            action: matchedAction.action as any
        }]);
        setStatus('connected');
    };

    const handleSendMessage = () => {
        if (!inputValue.trim() || status !== 'connected') return;
        executeAction(inputValue.trim());
    };

    return (
        <div className={`min-h-[100dvh] flex flex-col pt-16 ${isDark ? 'bg-[#0A0A0B] text-white' : 'bg-slate-50 text-slate-900'}`}>
            
            <main className="flex-1 w-full pt-16 flex flex-col lg:flex-row overflow-hidden absolute inset-0">
                {/* Left Panel: 3D Viewport & Metadata */}
                <div className={`flex-1 flex flex-col min-h-0 border-r ${isDark ? 'border-white/5 bg-[#0D0D10]' : 'border-slate-200 bg-white'}`}>
                    
                    {/* Header */}
                    <div className="relative px-6 py-5 shrink-0 z-20">
                        <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 via-orange-500/5 to-transparent pointer-events-none" />
                        <div className="relative flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shadow-lg shadow-red-500/20">
                                    <Cuboid className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-red-400 to-rose-500 tracking-tight">DesignInsight SKP-Agent</h1>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className={`text-[10px] font-semibold border px-1.5 py-0.5 rounded uppercase tracking-wider ${isDark ? 'bg-red-500/10 border-red-500/20 text-red-400' : 'bg-red-50 border-red-200 text-red-600'}`}>Ruby MCP</span>
                                        <span className={`text-[10px] ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>Direct API Interaction Server</span>
                                    </div>
                                </div>
                            </div>
                            
                            {status === 'idle' ? (
                                <button 
                                    onClick={connectToModel}
                                    className="px-5 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-all shadow-lg shadow-red-500/20 flex items-center gap-2 active:scale-95"
                                >
                                    <Server className="w-4 h-4" /> Connect NAS Workspace
                                </button>
                            ) : (
                                <div className={`px-4 py-2 rounded-lg text-xs flex items-center gap-2.5 shadow-inner ${isDark ? 'bg-black/40 border border-white/5 text-zinc-300' : 'bg-slate-100 border border-slate-200 text-slate-700'}`}>
                                    <span className="relative flex h-2 w-2">
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                    </span>
                                    <span className="font-mono">{modelData?.name}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Content Scrollable Area */}
                    <div className="flex-1 overflow-y-auto p-6 pt-0 flex flex-col gap-6">
                        
                        {/* Viewport Area */}
                        <div className={`w-full min-h-[360px] lg:h-[400px] shrink-0 rounded-2xl border relative overflow-hidden group shadow-sm ${isDark ? 'border-white/5 bg-zinc-900/50' : 'border-slate-200 bg-slate-100'}`}>
                            {status === 'idle' ? (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
                                    <Layers className={`w-16 h-16 mb-4 opacity-50 ${isDark ? 'text-zinc-700' : 'text-slate-300'}`} />
                                    <h3 className={`font-bold mb-2 text-lg ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>NAS 연결 대기중</h3>
                                    <p className={`text-xs max-w-sm leading-relaxed ${isDark ? 'text-zinc-600' : 'text-slate-400'}`}>SnapGRID NAS를 연결하여 스케치업 모델 데이터를 뷰포트에 동기화하고, 에이전트의 직접 제어를 허용하세요.</p>
                                </div>
                            ) : (
                                <div className="absolute inset-0 flex items-center justify-center bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-zinc-800 via-zinc-950 to-black overflow-hidden">
                                    {/* 3D Grid Floor */}
                                    <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px] [transform:rotateX(60deg)_translateY(-50px)_scale(2)] [transform-origin:center_center]" style={{perspective: '1000px'}} />
                                    
                                    {/* Mock 3D Viewport Object (Building Abstract) */}
                                    <div className="relative w-72 h-48 animate-[spin_25s_linear_infinite]" style={{ transformStyle: 'preserve-3d', transform: 'rotateX(-15deg) rotateY(25deg)' }}>
                                        <div className="absolute inset-x-0 inset-y-16 border border-rose-500/50 bg-rose-500/10 backdrop-blur-sm" style={{ transform: 'translateZ(40px)' }} />
                                        <div className="absolute inset-x-8 inset-y-0 border border-red-500/30 bg-red-500/5" style={{ transform: 'translateZ(-40px)' }} />
                                        <div className="absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-red-500/20 to-transparent" style={{ transform: 'rotateY(-90deg) translateZ(40px) translateX(-20px)' }} />
                                    </div>
                                    
                                    {/* Model Metadata Floating Panels */}
                                    <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-md border border-white/10 rounded-xl p-3 max-w-xs shadow-2xl">
                                        <div className="text-[10px] text-zinc-400 font-mono tracking-tight leading-loose">
                                            <div className="flex justify-between border-b border-white/10 pb-1 mb-1"><span className="text-rose-400">polygons</span> <span className="text-white">{modelData?.faces.toLocaleString()}</span></div>
                                            <div className="flex justify-between border-b border-white/10 pb-1 mb-1"><span className="text-rose-400">edges</span> <span className="text-white">{modelData?.edges.toLocaleString()}</span></div>
                                            <div className="flex justify-between border-b border-white/10 pb-1 mb-1"><span className="text-rose-400">definitions</span> <span className="text-white">{modelData?.groups.toLocaleString()}</span></div>
                                            <div className="flex justify-between"><span className="text-rose-400">volume</span> <span className="text-white">{(modelData?.dimensions.width * modelData?.dimensions.depth * modelData?.dimensions.height).toFixed(0).toLocaleString()} m³</span></div>
                                        </div>
                                    </div>
                                    
                                    <div className="absolute bottom-4 left-4 flex gap-2 w-full max-w-[80%] overflow-hidden">
                                        {modelData?.materials.map((m:string, i:number) => (
                                            <div key={i} className="text-[10px] whitespace-nowrap px-2.5 py-1 rounded-md bg-black/60 border border-white/10 text-zinc-300 font-medium backdrop-blur-md shadow-lg flex items-center gap-1.5">
                                                <div className="w-2 h-2 rounded-full" style={{background:['#b0b0b0','#90c0df','#cabbb8','#d2b48c','#f0f0f0'][i]}} />
                                                {m}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Quick Tooling / Actions */}
                        {status !== 'idle' && (
                            <div className="shrink-0 flex flex-col gap-3">
                                <h3 className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Quick AI Tooling</h3>
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                    {QUICK_ACTIONS.map(action => (
                                        <button
                                            key={action.id}
                                            onClick={() => executeAction(action.prompt)}
                                            disabled={status === 'working'}
                                            className={`p-3 rounded-xl border flex flex-col items-center justify-center text-center gap-2 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${
                                                isDark 
                                                    ? 'bg-zinc-900/40 border-white/5 hover:bg-zinc-800 hover:border-red-500/30 text-zinc-300 hover:text-white shadow-sm' 
                                                    : 'bg-white border-slate-200 hover:border-red-300 hover:bg-slate-50 text-slate-700 shadow-sm'
                                            }`}
                                        >
                                            <action.icon className={`w-5 h-5 ${isDark ? 'text-rose-400' : 'text-red-500'}`} />
                                            <span className="text-[11px] font-bold">{action.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Ruby Logs Console */}
                        <div className={`flex-1 min-h-[220px] rounded-2xl border flex flex-col overflow-hidden shadow-inner ${isDark ? 'border-white/5 bg-[#08080A]' : 'border-slate-200 bg-[#0f172a] text-slate-300'}`}>
                            <div className={`px-4 py-2 border-b flex items-center justify-between text-xs font-mono font-semibold ${isDark ? 'border-white/10 bg-white/5' : 'border-slate-700 bg-slate-800 object-contain text-slate-300'}`}>
                                <span className="flex items-center gap-2"><Code className="w-4 h-4 text-rose-500" /> Ruby Extension Console</span>
                                <span className="flex h-2 w-2 rounded-full bg-rose-500" />
                            </div>
                            <div className="flex-1 p-5 overflow-y-auto font-mono text-[11px] leading-relaxed space-y-1.5 text-zinc-400 select-text">
                                {rubyLogs.length === 0 ? (
                                    <span className="text-zinc-600 italic">Connected to MCP port 4301. Awaiting Supex Ruby instructions...</span>
                                ) : (
                                    rubyLogs.map((log, i) => (
                                        <div key={i} className={`
                                            ${log.startsWith('>') ? (log.includes('[MCP]') ? 'text-emerald-400' : 'text-rose-400') : ''}
                                            ${log.includes('puts') || log.includes('model =') ? 'text-blue-300' : ''}
                                            ${log.includes('def ') || log.includes('do |') || log.includes('end') ? 'text-purple-400' : ''}
                                        `}>
                                            {log}
                                        </div>
                                    ))
                                )}
                                {status === 'working' && (
                                    <div className="flex items-center gap-2 text-rose-400 mt-4 animate-pulse">
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> JIT Compiling Ruby AST...
                                    </div>
                                )}
                                <div ref={endOfLogsRef} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Panel: Chat Interface */}
                <div className={`w-full lg:w-[420px] xl:w-[480px] h-full flex flex-col border-l shrink-0 shadow-[-10px_0_30px_-15px_rgba(0,0,0,0.1)] ${isDark ? 'border-white/5 bg-[#0A0A0E]' : 'border-slate-200 bg-white'}`}>
                    <div className={`px-5 py-4 border-b flex items-center gap-2.5 shadow-sm z-10 ${isDark ? 'border-white/5 bg-black/20' : 'border-slate-100 bg-white'}`}>
                        <Sparkles className="w-4 h-4 text-rose-500" />
                        <span className="text-[13px] font-bold tracking-wide uppercase">Assistant</span>
                    </div>

                    <div className="flex-1 p-5 overflow-y-auto space-y-6 bg-[url('/img/grid-pattern.svg')] bg-center [background-size:20px]">
                        {chatHistory.map((msg, idx) => (
                            <div key={idx} className={`flex gap-3 relative ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                                
                                {msg.role !== 'system' && (
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10 ${
                                        msg.role === 'user' 
                                            ? isDark ? 'bg-zinc-800 text-white' : 'bg-slate-800 text-white'
                                            : 'bg-gradient-to-br from-red-500 to-rose-600 text-white shadow-lg shadow-red-500/20'
                                    }`}>
                                        {msg.role === 'user' ? <Box className="w-4 h-4" /> : <Cpu className="w-4 h-4" />}
                                    </div>
                                )}
                                
                                <div className={`flex flex-col gap-2 max-w-[85%] ${msg.role === 'system' ? 'mx-auto w-full max-w-full' : ''}`}>
                                    <div className={`rounded-2xl px-4 py-3 text-[13px] leading-relaxed shadow-sm ${
                                        msg.role === 'user'
                                            ? isDark ? 'bg-zinc-800 text-zinc-100 rounded-tr-sm' : 'bg-slate-800 text-white rounded-tr-sm'
                                            : msg.role === 'system' 
                                                ? isDark ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-center text-xs' : 'bg-emerald-50 text-emerald-700 border border-emerald-200 text-center text-xs'
                                                : isDark ? 'bg-zinc-900 border border-white/10 text-zinc-300 rounded-tl-sm' : 'bg-white text-slate-700 border border-slate-200 rounded-tl-sm'
                                    }`}>
                                        {msg.content}
                                    </div>
                                    
                                    {/* Action Buttons for specific responses */}
                                    {msg.action === 'boq_export' && (
                                        <button className={`w-full mt-1 px-4 py-2.5 rounded-xl border flex items-center justify-center gap-2 text-xs font-bold transition-colors ${
                                            isDark ? 'bg-rose-500/10 border-rose-500/30 text-rose-400 hover:bg-rose-500/20' : 'bg-red-50 border-red-200 text-red-600 hover:bg-red-100'
                                        }`}>
                                            <Share className="w-3.5 h-3.5" /> BOQ (물량산출) 에이전트로 데이터 전송
                                        </button>
                                    )}
                                    {msg.action === 'render_export' && (
                                        <button className={`w-full mt-1 px-4 py-2.5 rounded-xl border flex items-center justify-center gap-2 text-xs font-bold transition-colors ${
                                            isDark ? 'bg-blue-500/10 border-blue-500/30 text-blue-400 hover:bg-blue-500/20' : 'bg-blue-50 border-blue-200 text-blue-600 hover:bg-blue-100'
                                        }`}>
                                            <Share className="w-3.5 h-3.5" /> Render 에이전트 연동 (Enscape)
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                        <div ref={endOfMessagesRef} />
                    </div>

                    <div className={`p-4 border-t z-10 ${isDark ? 'border-white/5 bg-[#0C0C0E]' : 'border-slate-200 bg-white'}`}>
                        <div className={`relative rounded-xl border flex items-end overflow-hidden focus-within:ring-2 focus-within:ring-red-500/20 transition-all shadow-sm ${
                            isDark ? 'bg-zinc-900 border-white/10' : 'bg-slate-50 border-slate-300'
                        }`}>
                            <textarea 
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if(e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
                                }}
                                disabled={status !== 'connected'}
                                placeholder={status === 'connected' ? "명령을 입력하세요 (예: 불필요한 레이어 정리해줘)..." : "NAS 모델을 먼저 마운트해주세요."}
                                className={`w-full max-h-32 min-h-[48px] py-3 pl-4 resize-none bg-transparent outline-none text-[13px] ${
                                    isDark ? 'text-zinc-100 placeholder:text-zinc-600' : 'text-slate-900 placeholder:text-slate-400'
                                } disabled:opacity-50`}
                                rows={1}
                            />
                            <div className="p-2 pb-2">
                                <button 
                                    onClick={handleSendMessage}
                                    disabled={!inputValue.trim() || status !== 'connected'}
                                    className={`p-2.5 rounded-lg text-white transition-all ${
                                        inputValue.trim() && status === 'connected' ? 'bg-red-500 hover:bg-red-600 shadow-md' : 'bg-zinc-600 opacity-50 cursor-not-allowed'
                                    }`}
                                >
                                    {status === 'working' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

            </main>
        </div>
    );
}
